import { Box, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { Field, FieldArray, useFormikContext } from 'formik';
import { camelCase, find, findIndex, get, map } from 'lodash';
import { useMemo } from 'react';

import { InputComponent } from '..';
import { SelectableChip } from '../../selectable-chips/selectable-chip';
import { normalizeSize } from '../helpers';

export type InputChipOption = {
  value: string;
  label: string;
  disabled?: boolean;
  sortValue?: number;
  isActive?: boolean;
  isEditable?: boolean;
  isDeletable?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
};

export interface InputChipsProps extends InputComponent {
  label?: string;
  className?: string;
  size?: 'sm';
  options?: InputChipOption[];
  gap?: number;
  action?: (option: InputChipOption) => void;
  flatValues?: boolean; // if true, the value stored in formik is an array of strings instead of objects
}

export function InputChips({
  disabled,
  hidden,
  label,
  readOnly,
  required,
  nameOverride,
  size: propSize,
  hint,
  action,
  options = [],
  gap = 1,
  flatValues = false,
}: InputChipsProps) {
  const { errors, setFieldTouched, values } =
    useFormikContext<Record<string, any>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const fieldValues = get(values, name) ?? [];
  const size = normalizeSize(propSize);

  const isSelected = (option: InputChipOption) => {
    const found = flatValues
      ? fieldValues.includes(option.value)
      : find(fieldValues, { value: option.value });
    return Boolean(found || option.isActive);
  };

  return (
    <FormControl fullWidth>
      {label && <InputLabel shrink>{label}</InputLabel>}
      <Box
        gap={gap}
        display="flex"
        flexWrap="wrap"
        sx={{ mt: label ? 1.5 : 0 }}
      >
        <FieldArray
          name={name}
          render={({ remove, push }) =>
            map(options, (o) => (
              <Field
                as={SelectableChip}
                isInvalid={!!error}
                disabled={o.disabled || disabled}
                id={o.value}
                key={o.value}
                label={o.label}
                size={size}
                selected={isSelected(o)}
                onSelect={() => {
                  if (readOnly || disabled || o.disabled) return;
                  setFieldTouched(name, true);

                  if (action) {
                    return action(o);
                  }

                  const foundIdx = flatValues
                    ? fieldValues.indexOf(o.value)
                    : findIndex(fieldValues, { value: o.value });
                  if (foundIdx > -1) {
                    remove(foundIdx);
                  } else {
                    push(flatValues ? o.value : o);
                  }
                }}
                isEditable={o.isEditable}
                onEdit={o.onEdit}
                isDeletable={o.isDeletable}
                onDelete={o.onDelete}
              />
            ))
          }
        />
      </Box>
      {(!!error || !!hint) && (
        <FormHelperText error={!!error}>
          {(error as string) || hint}
        </FormHelperText>
      )}
    </FormControl>
  );
}
